import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Card, InputGroup } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaLock, FaKey } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function ProfileEditPage({ adminData, onProfileUpdate }) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', content: '' });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (adminData) {
            setFormData({
                name: adminData.name || '',
                email: adminData.email || '',
                current_password: '',
                new_password: '',
                new_password_confirmation: ''
            });
        }
    }, [adminData]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';

        if (formData.new_password) {
            if (formData.new_password.length < 8) {
                newErrors.new_password = 'Password must be at least 8 characters';
            }
            if (formData.new_password !== formData.new_password_confirmation) {
                newErrors.new_password_confirmation = 'Passwords do not match';
            }
            if (!adminData?.canResetWithoutCurrent && !formData.current_password) {
                newErrors.current_password = 'Current password is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setMessage({ type: '', content: '' });

        const token = localStorage.getItem('adminToken');
        if (!token) {
            setMessage({
                type: 'danger',
                content: 'Session expired. Please log in again.',
            });
            setTimeout(() => navigate('/login'), 3000);
            return;
        }

        try {
            const payload = {
                name: formData.name,
            };

            if (formData.new_password) {
                payload.new_password = formData.new_password;
                payload.new_password_confirmation = formData.new_password_confirmation;
                if (!adminData?.canResetWithoutCurrent) {
                    payload.current_password = formData.current_password;
                }
            }

            // For admin resets on others
            if (adminData?.targetAdminId && adminData?.canResetWithoutCurrent) {
                payload.admin_id = adminData.targetAdminId;
            }

            const response = await axios.put(
                'https://agpipefitting.org.et/api/admin/update-profile',
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.status === 200) {
                const updatedAdmin = response.data.admin;
                const successMessage = formData.new_password
                    ? 'Password updated successfully!'
                    : 'Profile updated successfully!';

                setMessage({
                    type: 'success',
                    content: (
                        <div>
                            {successMessage}
                            {formData.new_password && (
                                <div className="mt-2">
                                    <small>
                                        <strong>Note:</strong> Use your new password next time you log in.
                                    </small>
                                </div>
                            )}
                        </div>
                    ),
                });

                setFormData(prev => ({
                    ...prev,
                    current_password: '',
                    new_password: '',
                    new_password_confirmation: ''
                }));

                onProfileUpdate(updatedAdmin);
            }
        } catch (error) {
            let errorMessage = 'Something went wrong. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.errors) {
                errorMessage = Object.values(error.response.data.errors).join(' ');
            }

            setMessage({ type: 'danger', content: errorMessage });

            if (error.response?.status === 401) {
                localStorage.removeItem('adminToken');
                setTimeout(() => navigate('/login'), 3000);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrors(prev => ({ ...prev, [e.target.name]: '' }));
    };

    return (
        <Card className="shadow-lg p-4 border-0 rounded-3">
            <Card.Body>
                <h3 className="mb-3 text-center">Edit Profile</h3>

                {message.content && (
                    <Alert variant={message.type} className="mt-3">
                        {message.content}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit} className="mt-3">
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <InputGroup>
                            <InputGroup.Text><FaUser /></InputGroup.Text>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                isInvalid={!!errors.name}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.name}
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <InputGroup>
                            <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                readOnly
                                className="bg-light"
                            />
                        </InputGroup>
                        <Form.Text className="text-muted">
                            Contact support to change email.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <InputGroup>
                            <InputGroup.Text><FaKey /></InputGroup.Text>
                            <Form.Control
                                type="password"
                                name="new_password"
                                value={formData.new_password}
                                onChange={handleChange}
                                placeholder="Enter new password (optional)"
                                isInvalid={!!errors.new_password}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.new_password}
                            </Form.Control.Feedback>
                        </InputGroup>
                        <Form.Text className="text-muted">
                            Must be at least 8 characters.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Confirm New Password</Form.Label>
                        <InputGroup>
                            <InputGroup.Text><FaKey /></InputGroup.Text>
                            <Form.Control
                                type="password"
                                name="new_password_confirmation"
                                value={formData.new_password_confirmation}
                                onChange={handleChange}
                                placeholder="Confirm new password"
                                isInvalid={!!errors.new_password_confirmation}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.new_password_confirmation}
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>

                    {!adminData?.canResetWithoutCurrent && (
                        <Form.Group className="mb-3">
                            <Form.Label>Current Password</Form.Label>
                            <InputGroup>
                                <InputGroup.Text><FaLock /></InputGroup.Text>
                                <Form.Control
                                    type="password"
                                    name="current_password"
                                    value={formData.current_password}
                                    onChange={handleChange}
                                    placeholder="Enter current password"
                                    isInvalid={!!errors.current_password}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.current_password}
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                    )}

                    <div className="d-flex justify-content-end mt-4">
                        <Button 
                            variant="primary" 
                            type="submit" 
                            disabled={loading} 
                            className="px-4 py-2"
                        >
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Updating...
                                </>
                            ) : 'Save Changes'}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
}

export default ProfileEditPage;
